import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import Contact from "./contact";
import History from "./history";
import AgentPopUp from "./agentFilter";
import { useStateValue } from "../../context/StateProvider";
import ClientTypePopUp from "./ClientTypePopUp";
import LastActivityPopUp from "./LastActivityPopUp";
import TransactionCountPopUp from "./TransactionCountPopUp";
import { formatCurrency, formatNumberShort } from "../../utils/helper";
import { getAllContactsFromFirebase } from "../../utils/firebasefunctions";

export default function ClientsTab(props) {
  const [{ users, user, steps, leadsource, employees }, dispatch] =
    useStateValue();

  const filterButtons = [
    "All Clients",
    "Active Buyers",
    "Active Sellers",
    "Past Clients",
  ];

  // Track which button is active
  const [activeFilter, setActiveFilter] = useState("All Clients");
  const [activeTab, setActiveTab] = useState("Cards");
  const [isContact, setIsContact] = useState(false);
  const [isHistory, setIsHistory] = useState(false);
  const [selectedClient, setSelectedClient] = useState();
  const [searchQuery, setSearchQuery] = useState("");
  const [agentpopup, setagentpopup] = useState(false);
  const [selectedagent, setSelectedagent] = useState("All Agents");
  const [agentname, setagentname] = useState("All Agents");
  const [clientTypePopup, setClientTypePopup] = useState(false);
  const [selectedClientType, setSelectedClientType] = useState("All Types");
  const [lastActivityPopup, setLastActivityPopup] = useState(false);
  const [transactionPopup, setTransactionPopup] = useState(false);
  const [firebaseContacts, setFirebaseContacts] = useState([]);

  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (props.deals?.length > 0 && firebaseContacts?.length > 0) {
      const processed = processClients(props.deals, firebaseContacts);
      setClients(processed);
    }
  }, [props.deals, firebaseContacts]);

  const processClients = (deals, contacts) => {
    const map = {};

    // 1. Create contact lookup map (ID based)
    const contactMap = {};
    contacts?.forEach((c) => {
      contactMap[c.id] = c;
    });

    deals.forEach((deal) => {
      deal.customers?.forEach((customerId) => {
        const cust = contactMap[customerId];
        if (!cust) return; // skip if no contact found

        const key = cust.id; // now using ID instead of email

        if (!map[key]) {
          map[key] = {
            id: cust.id,
            phone: cust.phone,
            clientName: cust.name,
            email: cust.email,

            closedDealsCount: 0,
            activeDealsCount: 0,
            closedDealsVolume: 0,
            activeDealsVolume: 0,
            dealsList: [],
            roles: [],
            uniqueroles: [],
            closingDate: null,
            lastContactDays: null,
          };
        }

        const price = Number(deal.offerPrice || deal.salePrice || 0);

        // CLOSED DEALS
        if (deal.dealStage === "Closed") {
          map[key].closedDealsCount += 1;
          map[key].closedDealsVolume += price;

          if (deal.closingdate) {
            if (
              !map[key].closingDate ||
              new Date(deal.closingdate) > new Date(map[key].closingDate)
            ) {
              map[key].closingDate = deal.closingdate;
            }
          }
        }

        // ACTIVE DEALS
        if (["Active", "Pending", "Under Contract"].includes(deal.dealStage)) {
          map[key].activeDealsCount += 1;
          map[key].activeDealsVolume += price;
        }

        map[key].dealsList.push(deal);

        const role =
          deal.dealtype === "Seller"
            ? "Seller"
            : deal.dealtype === "Buyer"
              ? "Buyer"
              : "Unknown";

        map[key].roles.push({
          dealId: deal.id,
          role,
        });

        map[key].uniqueroles = map[key].roles.filter(
          (r, index, self) =>
            index === self.findIndex((x) => x.role === r.role),
        );
      });
    });

    // LAST CONTACT DAYS
    Object.values(map).forEach((client) => {
      if (client.closingDate) {
        const today = new Date();
        const closeDate = new Date(client.closingDate);

        const diffMs =
          today.setHours(0, 0, 0, 0) - closeDate.setHours(0, 0, 0, 0);

        client.lastContactDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      } else {
        client.lastContactDays = null;
      }
    });

    return Object.values(map);
  };

  const handleContactModal = (data) => {
    setSelectedClient(data);
    setIsContact(true);
  };

  const handleHistoryModal = (data) => {
    setSelectedClient(data);
    setIsHistory(true);
  };

  const filters = [
    {
      label: "Client Type",
      options: ["All Types", "Buyer", "Seller", "Both", "Investor"],
    },
    { label: "Agent", options: ["All Agents", "Henry", "Scott", "Joe"] },
    {
      label: "Last Activity",
      options: [
        "Any Time",
        "This Week",
        "This Month",
        "Last 3 Months",
        "Last 6 Months",
      ],
    },
    {
      label: "Transaction Count",
      options: ["Any", "First Time", "2-3 Transactions", "4+ Transactions"],
    },
  ];

  // Track selected options for each filter
  const [activeFilters, setActiveFilters] = useState({
    "Client Type": "All Types",
    Agent: "All Agents",
    "Last Activity": "Any Time",
    "Transaction Count": "Any",
  });

  const formatDaysAgo = (days) => {
    if (days === 0) return "Today";
    if (days === 1) return "1 day ago";
    if (days < 7) return `${days} days ago`; // < 7 days
    if (days < 30) return `${days} days ago`; // 7–29 days → still show in days

    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;

    const years = Math.floor(days / 365);
    return `${years} year${years > 1 ? "s" : ""} ago`;
  };

  const getInitials = (name) => {
    if (!name) return "NA";

    const parts = name.trim().split(" ").filter(Boolean);

    if (parts.length === 1) {
      // single name → first 2 letters
      return parts[0].slice(0, 2).toUpperCase();
    }

    // otherwise → first two words ka first letter
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  // Handle selection change
  const handleSelect = (label, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [label]: value,
    }));
  };

  const filteredClients = clients.filter((client) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = client.clientName?.toLowerCase().includes(query);
      const matchesPhone = client.phone?.toLowerCase().includes(query);
      const matchesEmail = client.email?.toLowerCase().includes(query);

      const matchesProperty = client.dealsList?.some((deal) =>
        deal.propertyaddress?.toLowerCase().includes(query),
      );

      if (!matchesName && !matchesPhone && !matchesEmail && !matchesProperty)
        return false;
    }

    const today = new Date();
    const closedDeals = client.dealsList.filter(
      (d) => d.dealStage === "Closed",
    );
    const activeDeals = client.dealsList.filter(
      (d) =>
        !["closed", "cancelled", "expired"].includes(d.dealStage.toLowerCase()),
    );

    let pass = true;

    // --- activeFilter ---
    if (activeFilter === "Active Buyers") {
      pass = activeDeals.some((deal) => deal.dealtype === "Buyer");
    } else if (activeFilter === "Active Sellers") {
      pass = activeDeals.some((deal) => deal.dealtype === "Seller");
    } else if (activeFilter === "Past Clients") {
      pass = client.closingDate && new Date(client.closingDate) < today;
    } // "All Clients" → no change, pass stays true

    // --- Agent filter ---
    if (pass && selectedagent !== "All Agents") {
      pass = client.dealsList.some(
        (deal) =>
          deal.assignedteamlead === selectedagent ||
          deal.agentData === selectedagent,
      );
    }

    // --- Client Type filter ---
    if (pass && selectedClientType !== "All Types") {
      pass = client.roles.some((r) => r.role === selectedClientType);
    }

    // --- Last Activity filter ---
    if (pass && activeFilters["Last Activity"] !== "Any Time") {
      const daysMap = {
        "This Week": 7,
        "This Month": 30,
        "Last 3 Months": 90,
        "Last 6 Months": 180,
      };
      const daysLimit = daysMap[activeFilters["Last Activity"]] || 0;
      pass = closedDeals.some(
        (deal) =>
          (today - new Date(deal.closingdate)) / (1000 * 60 * 60 * 24) <=
          daysLimit,
      );
    }

    // --- Transaction Count filter ---
    if (pass && activeFilters["Transaction Count"] !== "Any") {
      const count =
        activeFilter === "Active Buyers" || activeFilter === "Active Sellers"
          ? activeDeals.length
          : closedDeals.length;

      if (activeFilters["Transaction Count"] === "First Time")
        pass = count === 1;

      if (activeFilters["Transaction Count"] === "2-3 Transactions")
        pass = count >= 2 && count <= 3;

      if (activeFilters["Transaction Count"] === "4+ Transactions")
        pass = count >= 4;
    }

    return pass;
  });

  const loadFirebaseContacts = async () => {
    try {
      const data = await getAllContactsFromFirebase(user.id);
      setFirebaseContacts(data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadFirebaseContacts();
    }
  }, [user?.id]);

  console.log('filteredClients', filteredClients)

  return (
    <>
      <section className="space-y-8">
        {/* Search Section */}
        <div className="bg-white rounded-2xl shadow p-6 space-y-6">
          {/* Main Search */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search clients by name, phone, email, or property..."
                className="w-full border-2 border-gray-200 rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:border-[#1E293B] text-gray-700"
              />
            </div>
            <button className="bg-gradient-to-b from-[#0f172a] to-[#1E293B] text-white font-semibold rounded-lg px-6 py-2.5 transition">
              Search
            </button>
          </div>

          {/* Filter Section */}
          <div className="border-t pt-6 space-y-4">
            <h4 className="text-sm font-semibold text-gray-500">
              CLIENT FILTERS
            </h4>

            <div className="flex flex-wrap gap-2">
              {filterButtons.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-4 py-1.5 rounded-full border text-sm transition-all duration-200
            ${
              activeFilter === filter
                ? "bg-gradient-to-b from-[#0f172a] to-[#1E293B] text-white border-[#1E293B] shadow-sm"
                : "bg-gray-100 text-gray-700 hover:border-[#1E293B]"
            }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {/* Client Type Filter */}
              <div className="w-full flex relative flex-col">
                <h1>Client Type</h1>

                <div
                  onClick={() => setClientTypePopup(!clientTypePopup)}
                  className="w-full border-2 border-gray-300 rounded-lg p-2 cursor-pointer"
                >
                  {selectedClientType}
                </div>

                <ClientTypePopUp
                  style={{ top: "70px" }}
                  visible={clientTypePopup}
                  activateoutsidecontainerclick={() =>
                    setClientTypePopup(false)
                  }
                  onClickcorrect={(value) => {
                    setSelectedClientType(value);
                    setClientTypePopup(false);
                  }}
                />
              </div>

              <div className="w-full flex relative flex-col">
                <h1>Agent</h1>

                <div
                  onClick={() => setagentpopup(!agentpopup)}
                  className={`w-full border-2 border-gray-300 ${
                    agentname === "" ? "text-gray-400" : "text-textcolor"
                  } rounded-lg p-2 cursor-pointer`}
                >
                  {agentname || "All Agents"}
                </div>

                <AgentPopUp
                  style={{ top: "70px" }}
                  visible={agentpopup}
                  data={employees.filter(
                    (emp) => emp.type !== 1 && emp.type !== 2,
                  )}
                  activateoutsidecontainerclick={() => setagentpopup(false)}
                  onClickcorrect={(value, name) => {
                    setSelectedagent(value);
                    setagentname(name);
                    setagentpopup(false);
                  }}
                />
              </div>
              <div className="w-full flex relative flex-col">
                <h1>Last Activity</h1>
                <div
                  onClick={() => setLastActivityPopup(!lastActivityPopup)}
                  className="w-full border-2 border-gray-300 rounded-lg p-2 cursor-pointer"
                >
                  {activeFilters["Last Activity"]}
                </div>
                <LastActivityPopUp
                  visible={lastActivityPopup}
                  style={{ top: "70px" }}
                  onClickcorrect={(value) => {
                    handleSelect("Last Activity", value);
                    setLastActivityPopup(false);
                  }}
                  activateoutsidecontainerclick={() =>
                    setLastActivityPopup(false)
                  }
                />
              </div>

              {/* Transaction Count Filter */}
              <div className="w-full flex relative flex-col">
                <h1>Transaction Count</h1>
                <div
                  onClick={() => setTransactionPopup(!transactionPopup)}
                  className="w-full border-2 border-gray-300 rounded-lg p-2 cursor-pointer"
                >
                  {activeFilters["Transaction Count"]}
                </div>
                <TransactionCountPopUp
                  visible={transactionPopup}
                  style={{ top: "70px" }}
                  onClickcorrect={(value) => {
                    handleSelect("Transaction Count", value);
                    setTransactionPopup(false);
                  }}
                  activateoutsidecontainerclick={() =>
                    setTransactionPopup(false)
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results Header */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-3">
          <div className="text-gray-700 text-lg">
            Found{" "}
            <strong className="text-[#1E293B]">{filteredClients.length}</strong>{" "}
            clients
          </div>
          <div className="flex gap-2">
            {["Cards", "Table"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 rounded-md text-sm border-2 transition duration-200
            ${
              activeTab === tab
                ? "bg-gradient-to-b from-[#0f172a] to-[#1E293B] text-white border-[#1E293B]"
                : "bg-white text-gray-700 border-gray-300 hover:border-[#1E293B]"
            }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Client Cards */}
        <div className="">
          {/* Card 1 */}
          {filteredClients.map((item) => (
            <div className="bg-white p-6 rounded-2xl shadow hover:shadow-lg transition mt-5">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-b from-[#0f172a] to-[#1E293B] text-white flex items-center justify-center text-xl font-bold">
                  {getInitials(item.clientName)}
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">
                    {item.clientName}
                  </h3>
                  <div className="flex gap-2 text-xs mt-1">
                    {item.uniqueroles.map((type, i) => (
                      <span
                        key={i}
                        className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                      >
                        {type.role}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-between text-center border-y border-gray-200 py-4 mb-4 w-[60%] mx-auto">
                <div>
                  <div className="text-[#1E293B] font-semibold text-xl">
                    {activeFilter === "Active Buyers" ||
                    activeFilter === "Active Sellers"
                      ? item.activeDealsCount
                      : item.closedDealsCount}
                  </div>
                  <div className="text-gray-500 text-xs">Transactions</div>
                </div>
                <div>
                  <div className="text-[#1E293B] font-semibold text-xl">
                    
                    {activeFilter === "Active Buyers" ||
                    activeFilter === "Active Sellers"
                      ? formatCurrency(item.activeDealsVolume)
                      : formatCurrency(item.closedDealsVolume)}
                  </div>
                  <div className="text-gray-500 text-xs">Total Volume</div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                <span className="text-gray-800 font-medium">Email:</span>{" "}
                {item.email} <br />
                {item.lastContactDays !== null && item.lastContactDays > 0 && (
                  <>
                    <span className="text-gray-800 font-medium mt-5">
                      Last Contact:
                    </span>{" "}
                    {formatDaysAgo(item.lastContactDays)}
                  </>
                )}
              </p>

              <div className="flex flex-wrap justify-end gap-4">
                <button
                  onClick={() => handleContactModal(item)}
                  className="border border-gray-200 px-3 py-1.5 rounded-md text-sm hover:bg--[#1E293B] transition"
                >
                  Contact
                </button>
                {(activeFilter === "Active Buyers" ||
                activeFilter === "Active Sellers"
                  ? item.activeDealsCount
                  : item.closedDealsCount) > 0 && (
                  <button
                    onClick={() => handleHistoryModal(item)}
                    className="border border-gray-200 px-3 py-1.5 rounded-md text-sm hover:bg-[#1E293B] hover:text-white transition"
                  >
                    History
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
      {isContact && (
        <Contact onClose={() => setIsContact(false)} data={selectedClient} />
      )}
      {isHistory && (
        <History
          onClose={() => setIsHistory(false)}
          data={selectedClient}
          users={users}
        />
      )}
    </>
  );
}
